import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from '../components/navigation/Navbar';
// import Sidebar from '../components/navigation/Sidebar';
import { usePlatformStore } from '../store/platformStore';
import { usePreloadStore } from '../store/preloadStore';
import { usePostLoginOnboarding } from '../features/auth/hooks/usePostLoginOnboarding';
import TwoFactorOnboardingModal from '../features/auth/components/TwoFactorOnboardingModal';
import TwoFactorSetupWizard from '../features/auth/components/TwoFactorSetupWizard';
import { PageErrorBoundary } from '../components/common/ErrorBoundary';
import { useSetupGlobalNotifications } from '../contexts/ErrorNotificationContext';
import { ChatWidget } from '../components/chat';

const MainAppLayout: React.FC = () => {
  const { activeView, setActiveView } = usePlatformStore();
  const userInfo = usePreloadStore((s) => s.getUserInfo)();
  const isStaff = userInfo?.user?.is_staff || false;
  const location = useLocation();
  const [show2FASetup, setShow2FASetup] = useState(false);
  
  // Set up global notifications
  useSetupGlobalNotifications();
  
  // Use the post-login onboarding hook
  const {
    show2FAPrompt,
    loading: onboardingLoading,
    handleSetup2FA: onboardingSetup2FA,
    handleSkip2FA: onboardingSkip2FA,
    handleDontShowAgain: onboardingDontShowAgain
  } = usePostLoginOnboarding();

  useEffect(() => {
    const path = location.pathname.split('/')[1] || 'autopilot';
    // Ensure this mapping matches your actual view names and paths
    const viewName = {
      'autopilot': 'Autopilot',
      'copilot': 'Copilot',
      'workbench': 'Workbench',
      'platforms': 'PlatformDashboard',
      'job-types': 'JobTypes',
      'jobs': 'Jobs',
      'job-runner': 'JobRunner',
      'endpoints': 'Endpoints',
      'configurations': 'Configurations',
      'ssot': 'SSOT',
      'transform': 'Transform',
      'load': 'Load',
    }[path] || 'Autopilot'; // Default to 'Autopilot'
    setActiveView(viewName);
  }, [location, setActiveView]);

  const handleSetup2FA = async () => {
    await onboardingSetup2FA();
    setShow2FASetup(true);
  };

  const handleSetup2FACancel = () => {
    setShow2FASetup(false);
  };

  const handleSetup2FAComplete = () => {
    setShow2FASetup(false);
  };

  return (
    <div className="flex flex-col h-screen transition-colors duration-200 bg-transparent">
      <Navbar activeTab={activeView} />
      <div className="flex overflow-hidden flex-1">
        {/* <Sidebar activeTab={activeView} /> */}
        <main className="overflow-auto flex-1 p-6 bg-transparent">
          <PageErrorBoundary>
            <Outlet /> {/* Child routes will render here */}
          </PageErrorBoundary>
        </main>
      </div>
      
      {/* 2FA Onboarding Modal */}
      <TwoFactorOnboardingModal
        isOpen={show2FAPrompt}
        onClose={onboardingSkip2FA}
        onSetup={handleSetup2FA}
        onSkip={onboardingSkip2FA}
        onDontShowAgain={onboardingDontShowAgain}
      />
      
      {/* 2FA Setup Wizard */}
      <TwoFactorSetupWizard
        isOpen={show2FASetup}
        onClose={handleSetup2FACancel}
        onComplete={handleSetup2FAComplete}
      />
      
      {/* AI Chat Widget — admin only */}
      {isStaff && <ChatWidget />}

      {/* Loading overlay while checking onboarding status */}
      {onboardingLoading && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-opacity-50 bg-neutral-500">
          <div className="p-6 rounded-lg shadow-xl bg-theme-bg dark:bg-dark-theme-bg">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 rounded-full border-b-2 animate-spin border-primary-light dark:border-primary-dark"></div>
              <span className="text-theme-text dark:text-dark-theme-text">Checking account security...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainAppLayout;
